import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export type LeavePolicyRuleRecord = {
  id?: string;
  leaveTypeId?: string;
  ruleId?: string;
  ruleKey: string;
  ruleValue: string;
  ruleType: string;
  description: string;
};

export type LeavePolicyRecord = {
  id: ID;
  name: string;
  isActive: boolean;
  leaveTypes: Array<{
    leaveTypeId: ID;
    title: string;
    slug: string;
    daysAllowed: number;
    maxDaysPerRequest: number;
    maxDaysPerMonth: number;
  }>;
  rules: LeavePolicyRuleRecord[];
  createdAt: string;
  updatedAt: string;
};

export type LeaveRuleCatalogItem = {
  id: string;
  code: string;
  title: string;
  description: string;
  type: "leave";
  global_value: unknown;
  data_type: string;
};

export type LeavePolicyLinkedRuleApi = {
  id?: string | number;
  leave_type_id?: string | number;
  rule_id?: string | number;
  rule_key?: string;
  rule_value?: string;
  data_type?: string;
  description?: string | null;
};

const LS_KEY = "inventory.leavePolicies.v1";

type ApiRow = {
  id?: number | string;
  name?: string | null;
  is_active?: boolean;
  leave_types?: Array<{
    leave_type_id?: number | string;
    title?: string | null;
    slug?: string | null;
    days_allowed?: number | string | null;
    max_days_per_request?: number | string | null;
    max_days_per_month?: number | string | null;
  }> | null;
  rules?: Array<{
    id?: number | string;
    leave_type_id?: number | string;
    rule_id?: number | string;
    rule_key?: string | null;
    rule_value?: string | null;
    rule_type?: string | null;
    description?: string | null;
  }> | null;
  created_at?: string | null;
  updated_at?: string | null;
};

function load(): LeavePolicyRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as LeavePolicyRecord[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function save(items: LeavePolicyRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(items));
}

function extractRows(body: unknown): ApiRow[] {
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (Array.isArray(data)) return data as ApiRow[];
  return [];
}

function str(v: unknown): string {
  if (v == null || v === "") return "";
  return String(v);
}

function mapRow(r: ApiRow): LeavePolicyRecord {
  return {
    id: String(r.id ?? ""),
    name: String(r.name ?? "").trim(),
    isActive: r.is_active === undefined || r.is_active === null ? true : Boolean(r.is_active),
    leaveTypes: Array.isArray(r.leave_types)
      ? r.leave_types
          .filter((x) => x && x.leave_type_id != null)
          .map((x) => ({
            leaveTypeId: String(x.leave_type_id ?? ""),
            title: String(x.title ?? "").trim(),
            slug: String(x.slug ?? "").trim(),
            daysAllowed: Number(x.days_allowed ?? 0) || 0,
            maxDaysPerRequest: Number(x.max_days_per_request ?? 0) || 0,
            maxDaysPerMonth: Number(x.max_days_per_month ?? 0) || 0,
          }))
      : [],
    rules: Array.isArray(r.rules)
      ? r.rules
          .filter((x) => x && (x.rule_id != null || x.rule_key))
          .map((x) => ({
            id: x.id != null ? String(x.id) : undefined,
            leaveTypeId: x.leave_type_id != null ? String(x.leave_type_id) : undefined,
            ruleId: x.rule_id != null ? String(x.rule_id) : undefined,
            ruleKey: String(x.rule_key ?? ""),
            ruleValue: String(x.rule_value ?? ""),
            ruleType: String(x.rule_type ?? "leave"),
            description: String(x.description ?? ""),
          }))
      : [],
    createdAt: String(r.created_at ?? new Date().toISOString()),
    updatedAt: String(r.updated_at ?? r.created_at ?? new Date().toISOString()),
  };
}

export function leavePolicyErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong. Please try again.";
}

export function listLeavePolicies(): LeavePolicyRecord[] {
  return load().sort((a, b) => a.name.localeCompare(b.name) || String(b.id).localeCompare(String(a.id)));
}

export async function syncLeavePoliciesFromApi(): Promise<LeavePolicyRecord[]> {
  const response = await api.get("/api/v1/leave-policies", { params: { page: 1, per_page: 200 } });
  const mapped = extractRows(response.data).map(mapRow).filter((x) => Boolean(x.id));
  save(mapped);
  return listLeavePolicies();
}

function parseLeaveRulesSetupBody(body: unknown): {
  catalog: LeaveRuleCatalogItem[];
  linked: LeavePolicyLinkedRuleApi[];
} {
  const block = body && typeof body === "object" && "data" in body ? (body as { data?: unknown }).data : body;
  const b = block && typeof block === "object" ? (block as Record<string, unknown>) : {};
  const catalogRaw = Array.isArray(b.catalog) ? b.catalog : [];
  const linkedRaw = Array.isArray(b.linked) ? b.linked : [];
  const catalog: LeaveRuleCatalogItem[] = catalogRaw.map((row) => {
    const r = row as Record<string, unknown>;
    return {
      id: str(r.id),
      code: str(r.code),
      title: str(r.title),
      description: str(r.description),
      type: "leave",
      global_value: r.global_value,
      data_type: str(r.data_type) || "string",
    };
  });
  return { catalog, linked: linkedRaw as LeavePolicyLinkedRuleApi[] };
}

export async function fetchLeaveRulesCatalog(): Promise<{
  catalog: LeaveRuleCatalogItem[];
  linked: LeavePolicyLinkedRuleApi[];
}> {
  const response = await api.get(`/api/v1/leave-policies/leave-rules-catalog`);
  return parseLeaveRulesSetupBody(response.data);
}

export async function fetchLeaveRulesSetup(
  policyId: ID,
  leaveTypeId?: ID,
): Promise<{
  catalog: LeaveRuleCatalogItem[];
  linked: LeavePolicyLinkedRuleApi[];
}> {
  const response = await api.get(`/api/v1/leave-policies/${encodeURIComponent(String(policyId))}/leave-rules-setup`, {
    params: leaveTypeId ? { leave_type_id: leaveTypeId } : undefined,
  });
  return parseLeaveRulesSetupBody(response.data);
}

export type LeaveTypeRulesPayload = {
  leave_type_id: number | string;
  rules: Array<{ rule_id: number | string; rule_value: string; rule_type?: string; description?: string }>;
};

export async function createLeavePolicy(data: {
  name: string;
  isActive?: boolean;
  leaveTypeId?: ID;
  rules?: Array<{ rule_id: number | string; rule_value: string; rule_type?: string; description?: string }>;
  leaveTypeRules?: LeaveTypeRulesPayload[];
}): Promise<{ record: LeavePolicyRecord; message: string }> {
  const payload: Record<string, unknown> = {
    name: data.name.trim(),
    is_active: data.isActive !== false,
  };
  if (data.leaveTypeRules !== undefined) {
    payload.leave_type_rules = data.leaveTypeRules.map((g) => ({
      leave_type_id: Number(g.leave_type_id),
      rules: g.rules,
    }));
  } else {
    if (data.leaveTypeId !== undefined) {
      payload.leave_type_id = Number(data.leaveTypeId);
    }
    if (data.rules !== undefined) {
      payload.rules = data.rules;
    }
  }
  const response = await api.post<{ message?: string; data?: ApiRow }>("/api/v1/leave-policies", payload);
  const rec = response.data?.data;
  if (!rec?.id) throw new Error("Invalid create leave policy response.");
  const record = mapRow(rec);
  const next = listLeavePolicies().filter((x) => x.id !== record.id);
  next.push(record);
  save(next);
  return { record, message: response.data?.message || "Leave policy created." };
}

export async function updateLeavePolicy(
  id: ID,
  data: Partial<{
    name: string;
    isActive: boolean;
    leaveTypeId: ID;
    rules: Array<{ rule_id: number | string; rule_value: string; rule_type?: string; description?: string }>;
    leaveTypeRules: LeaveTypeRulesPayload[];
  }>,
): Promise<{ record: LeavePolicyRecord; message: string } | null> {
  const items = listLeavePolicies();
  const idx = items.findIndex((x) => x.id === id);
  if (idx === -1) return null;
  const payload: Record<string, unknown> = {};
  if (data.name !== undefined) payload.name = data.name.trim();
  if (data.isActive !== undefined) payload.is_active = Boolean(data.isActive);
  if (data.leaveTypeRules !== undefined) {
    payload.leave_type_rules = data.leaveTypeRules.map((g) => ({
      leave_type_id: Number(g.leave_type_id),
      rules: g.rules,
    }));
  } else {
    if (data.leaveTypeId !== undefined) payload.leave_type_id = Number(data.leaveTypeId);
    if (data.rules !== undefined) payload.rules = data.rules;
  }
  const response = await api.put<{ message?: string; data?: ApiRow }>(`/api/v1/leave-policies/${id}`, payload);
  const rec = response.data?.data;
  if (!rec?.id) throw new Error("Invalid update leave policy response.");
  const record = mapRow(rec);
  items[idx] = record;
  save(items);
  return { record, message: response.data?.message || "Leave policy updated." };
}

export async function deleteLeavePolicy(id: ID): Promise<{ success: boolean; message: string }> {
  const response = await api.delete<{ message?: string }>(`/api/v1/leave-policies/${id}`);
  const items = listLeavePolicies();
  const next = items.filter((y) => y.id !== id);
  save(next);
  return { success: next.length !== items.length, message: response.data?.message || "Deleted." };
}
